import { CurrencyFormatter } from "@/constants/format";
import { Box, HStack, Image, VStack } from "@chakra-ui/react";
import type { DTO } from "@equidefi/shared/types";
import { Heading, Text } from "@equidefi/ui";
import { Icon } from "@equidefi/ui/icon";
import type React from "react";
import { useMemo } from "react";

interface OfferingFolderProps {
  offering: DTO.Offering;
  issuer: DTO.Issuer;
  investments: DTO.Investment[];
  onOfferingClick: () => void;
}

/**
 * OfferingFolder - Card that displays offering information
 *
 * Features:
 * - Shows issuer logo, name, and uses issuer brand color
 * - Displays total amount invested and number of investments
 * - Shows offering name/description
 * - Clickable to view investments in this offering
 */
export const OfferingFolder: React.FC<OfferingFolderProps> = ({ offering, issuer, investments, onOfferingClick }) => {
  /**
   * Calculate total amount invested in this offering
   */
  const totalAmount = useMemo(() => {
    return investments.reduce((sum, inv) => sum + Number(inv.amount || 0), 0);
  }, [investments]);

  // Use issuer brand color or default
  const brandColor = issuer?.brand_color || "#3C0C94";

  // Get offering description/type text
  const offeringDescription = offering?.name || "";

  return (
    <Box
      as="button"
      aria-label={`View ${investments.length} investment${investments.length === 1 ? "" : "s"} in ${issuer?.name}`}
      borderRadius="md"
      bg={brandColor}
      overflow="hidden"
      cursor="pointer"
      onClick={onOfferingClick}
      transition="all 0.2s"
      _hover={{ transform: "translateY(-2px)", boxShadow: "lg" }}
      _focus={{ outline: "2px solid", outlineColor: "blue.500", outlineOffset: "2px" }}
      p="4"
      w="full"
      textAlign="left">
      <HStack spacing="3" color="white" mb="3">
        <Image
          src={issuer?.photo_url}
          alt={issuer?.name}
          boxSize="50px"
          borderRadius="md"
          bg="white"
          objectFit="contain"
          p="1"
        />

        <VStack flex="1" align="start" spacing="0.5">
          <Heading as="h3" size="md" fontWeight="bold" mb="0" noOfLines={1}>
            {issuer?.name}
          </Heading>
          <HStack spacing="2" fontSize="sm" opacity="0.9">
            <Text mb="0" fontWeight="bold">
              {CurrencyFormatter.format(totalAmount)}
            </Text>
            <Text mb="0">•</Text>
            <Text mb="0">
              {investments.length} {investments.length === 1 ? "investment" : "investments"}
            </Text>
          </HStack>
        </VStack>

        {/* Warning icon for incomplete investments */}
        {investments.some((inv) => inv.status === "PENDING") && (
          <Box bg="orange.400" borderRadius="full" p="2" display="flex" alignItems="center" justifyContent="center">
            <Icon.AlertCircle size="1.2em" />
          </Box>
        )}
      </HStack>

      {/* Offering description */}
      <Text fontSize="sm" mb="0" color="white" opacity="0.95" noOfLines={2}>
        {offeringDescription}
      </Text>
    </Box>
  );
};
